/**
 * Admin auth (separate from user magic-link auth).
 * Token lives in localStorage as `blendsafe_admin_token`.
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const TOKEN_KEY = "medskin_admin_token";

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  // null = checking, false = not authed, object = authed admin
  const [admin, setAdmin] = useState(null);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setAdmin(false); return; }
    try {
      const { data } = await axios.get(`${API}/admin/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAdmin(data);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setAdmin(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(
      `${API}/admin/auth/login`,
      { email, password },
    );
    localStorage.setItem(TOKEN_KEY, data.access_token);
    setAdmin(data.admin);
    return data.admin;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setAdmin(false);
  }, []);

  return (
    <AdminAuthContext.Provider value={{ admin, login, logout, fetchMe }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used inside <AdminAuthProvider>");
  return ctx;
}

/** Axios instance pre-wired with the admin Bearer token. */
export function adminApi() {
  const token = localStorage.getItem(TOKEN_KEY);
  return axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}
